import * as React from 'react'
import { cn } from '@/lib/utils'
import { ToastContext } from './toast-context'
import { motion, AnimatePresence } from 'framer-motion'

type Toast = { id: string; message: string | React.ReactNode; type?: 'success' | 'error' | 'warning' | 'info'; duration?: number }

export function ToastProvider({ children }: { children: React.ReactNode }) {
  const [toasts, setToasts] = React.useState<Toast[]>([])

  const push = React.useCallback((message: string | React.ReactNode, type: Toast['type'] = 'info', duration = 3000) => {
    const id = crypto.randomUUID()
    const toast: Toast = { id, message, type, duration }
    setToasts(prev => [...prev, toast])
    if (duration !== Infinity) {
      window.setTimeout(() => {
        setToasts(prev => prev.filter(t => t.id !== id))
      }, duration)
    }
    return id
  }, [])

  const remove = React.useCallback((id: string) => {
    setToasts(prev => prev.filter(t => t.id !== id))
  }, [])

  return (
    <ToastContext.Provider value={{ push }}>
      {children}
      <motion.div className="fixed bottom-4 right-4 z-[100] flex flex-col-reverse gap-2" layout>
        <AnimatePresence>
          {toasts.map(t => (
            <motion.div
              key={t.id}
              layout
              initial={{ y: 20, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              exit={{ x: 400, opacity: 0 }}
              transition={{ type: 'spring', stiffness: 300, damping: 25 }}
              drag="x"
              dragConstraints={{ left: 0, right: 0 }}
              dragElastic={0.1}
              onDragEnd={(event, info) => {
                if (info.offset.x > 100) {
                  remove(t.id)
                }
              }}
              className={cn(
                'min-w-[320px] max-w-[480px] rounded-lg border px-4 py-3 shadow-lg cursor-grab active:cursor-grabbing',
                'backdrop-blur-xs bg-zinc-900/80 border-zinc-800 text-white',
                t.type === 'success' && 'border-green-600/30',
                t.type === 'error' && 'border-red-600/30',
                t.type === 'warning' && 'border-yellow-600/30',
                t.type === 'info' && 'border-[rgb(var(--accent-1))]/30',
              )}
              role="status"
            >
              <div className="flex items-start justify-between gap-3">
                <div className="flex-1 text-sm leading-6 text-zinc-100">{t.message}</div>
                <button className="text-zinc-400 hover:text-white shrink-0" onClick={() => remove(t.id)} aria-label="Dismiss">
                  ×
                </button>
              </div>
            </motion.div>
          ))}
        </AnimatePresence>
      </motion.div>
    </ToastContext.Provider>
  )
}


